/**
 * @fileoverview The ONE heading-tag lookup (JSDOC-PROP-CONTRACT-001, Q-JPC-HEADING-ONE, coordinator-ruled).
 *
 * A surface that takes its heading level from its host used to build the tag as `` `h${level}` `` and the level
 * beneath it as `level + 1`. The first is a string no type checker can prove an element; the second is no level at
 * all once it passes 6. Every such surface asks these two instead, and an out-of-range level is refused BY NAME —
 * there is no `<h7>`, and nothing sits one level below an `<h6>`.
 *
 * It lives here because feedback-ui is the LEAF every heading-taking package already reaches (page-shell re-exports
 * it, identity pinned), and in its own module so it is not tangled with the feedback components.
 *
 * @module @puffin-core/feedback-ui/headingLevels
 */

/** @typedef {1|2|3|4|5|6} HeadingLevel */
/** @typedef {'h1'|'h2'|'h3'|'h4'|'h5'|'h6'} HeadingTag */

/**
 * Each level's tag, as an EXACT table type — so `headingTagFor` answers the tag OF its level (`2|3` → `'h2'|'h3'`),
 * never the whole union, and a component that takes levels 2–6 is handed only tags 2–6.
 *
 * @typedef {{1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6'}} HeadingTagTable
 */

/**
 * The level beneath each level that has one, as an EXACT table type — so `subHeadingLevelOf` answers the level
 * beneath ITS input (`2|3|4` → `3|4|5`), and a sub-section that takes levels 3–5 accepts it.
 *
 * @typedef {{1: 2, 2: 3, 3: 4, 4: 5, 5: 6}} SubHeadingLevelTable
 */

/** @type {Readonly<HeadingTagTable>} */
const HEADING_TAGS = Object.freeze({ 1: 'h1', 2: 'h2', 3: 'h3', 4: 'h4', 5: 'h5', 6: 'h6' });

/**
 * The level beneath each level that has one, stated rather than computed: `level + 1` is a number, not a level.
 *
 * @type {Readonly<SubHeadingLevelTable>}
 */
const SUB_HEADING_LEVELS = Object.freeze({ 1: 2, 2: 3, 3: 4, 4: 5, 5: 6 });

/**
 * The six heading levels, in order.
 *
 * @type {ReadonlyArray<HeadingLevel>}
 */
export const HEADING_LEVELS = Object.freeze([1, 2, 3, 4, 5, 6]);

/**
 * @param {*} value
 * @returns {string} how a refusal spells what it received: a string quoted, so `"2"` never reads as the number 2
 */
function spelled(value) {
  return typeof value === 'string' ? JSON.stringify(value) : String(value);
}

/**
 * The element a heading of `level` renders as.
 *
 * @template {HeadingLevel} L
 * @param {L} level
 * @returns {HeadingTagTable[L]}
 * @throws {TypeError} when `level` is not an integer from 1 to 6
 */
export function headingTagFor(level) {
  if (!Number.isInteger(level) || level < 1 || level > 6) {
    throw new TypeError(
      `headingTagFor: a heading level is an integer from 1 to 6, received ${spelled(level)}`,
    );
  }
  return HEADING_TAGS[level];
}

/**
 * The level of a heading one beneath `level` (a section's own sub-headings).
 *
 * @template {1|2|3|4|5} L
 * @param {L} level
 * @returns {SubHeadingLevelTable[L]}
 * @throws {TypeError} when `level` is not an integer from 1 to 5 — an `<h6>` has no level beneath it
 */
export function subHeadingLevelOf(level) {
  if (!Number.isInteger(level) || level < 1 || level > 5) {
    throw new TypeError(
      `subHeadingLevelOf: a heading with a level beneath it is an integer from 1 to 5, received ${spelled(level)}`,
    );
  }
  return SUB_HEADING_LEVELS[level];
}
